import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  ContentCopy as DuplicateIcon,
  History as HistoryIcon,
  Restore as RestoreIcon,
  CompareArrows as CompareIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { API_PATHS, API_BASE_URL } from '../../../utils/constants';

// Helper: decode JWT from localStorage and get username/subject
const getUsernameFromStoredToken = () => {
  const keys = ['jwt_token', 'jwtToken', 'token', 'authToken', 'auth_token'];
  let token = null;
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v) { token = v; break; }
  }
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return json.username || json.user || json.sub || null;
  } catch (e) {
    return null;
  }
};

// Helper: resolve display name for createdBy coming from backend
const resolveCreatedBy = (createdBy) => {
  // If backend returns a proper username or email, use it
  if (createdBy && typeof createdBy === 'string' && createdBy.length > 0 && !/^\s*$/.test(createdBy)) {
    return createdBy;
  }
  // Fallback to JWT subject (e.g., tkadmin)
  const fromToken = getUsernameFromStoredToken();
  if (fromToken) return fromToken;
  return 'Unknown';
};

// Simple JSON Editor component
const JsonEditor = ({ value, onChange, readOnly }) => {
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    try {
      const parsed = JSON.parse(e.target.value);
      setError(null);
      onChange(parsed);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      {readOnly ? (
        <pre style={{ background: '#272822', color: '#f8f8f2', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
          {JSON.stringify(value, null, 2)}
        </pre>
      ) : (
        <textarea
          value={JSON.stringify(value, null, 2)}
          onChange={handleChange}
          style={{
            width: '100%',
            minHeight: '300px',
            fontFamily: 'monospace',
            padding: '8px',
            border: error ? '1px solid red' : '1px solid #ccc'
          }}
        />
      )}
      {error && (
        <Typography color="error" variant="caption" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

// Step card component for visualization
const WorkflowStepCard = ({ step, dependencies }) => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {step.stepNumber > 0 ? `Step ${step.stepNumber}: ` : ''}{step.name}
          </Typography>
          <Box>
            <Chip
              label={step.isRequired ? 'Required' : 'Optional'}
              color={step.isRequired ? 'primary' : 'default'}
              size="small"
              sx={{ mr: 1 }}
            />
            {step.executionMode && (
              <Chip
                label={step.executionMode}
                color="info"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Box>
        
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {step.serviceId && (
            <Typography variant="body2" color="textSecondary">
              <strong>Service ID:</strong> {step.serviceId}
            </Typography>
          )}
          {step.serviceName && (
            <Typography variant="body2" color="textSecondary" sx={{ ml: step.serviceId ? 2 : 0 }}>
              <strong>Service:</strong> {step.serviceName}
            </Typography>
          )}
        </Box>
        
        {dependencies && dependencies.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Dependencies:</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {dependencies.map((dep, index) => (
                <Chip
                  key={index}
                  label={`${dep.stepName} (${dep.type})`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
        
        {/* Display additional properties if available */}
        {Object.entries(step)
          .filter(([key]) => !['id', 'name', 'serviceId', 'serviceName', 'isRequired', 'stepNumber', 'executionMode', 'dependencies'].includes(key))
          .length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Additional Properties:</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1 }}>
              {Object.entries(step)
                .filter(([key]) => !['id', 'name', 'serviceId', 'serviceName', 'isRequired', 'stepNumber', 'executionMode', 'dependencies'].includes(key))
                .map(([key, value]) => (
                  <Typography key={key} variant="body2" color="textSecondary">
                    <strong>{key}:</strong> {typeof value === 'object' ? JSON.stringify(value) : value.toString()}
                  </Typography>
                ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const WorkflowDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [workflowJson, setWorkflowJson] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncNotes, setSyncNotes] = useState('');
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [fieldChanges, setFieldChanges] = useState([]);

  // Function to normalize workflow data to a consistent format
  const normalizeWorkflowData = (workflow) => {
    console.log('Raw workflow data:', workflow);
    console.log('Workflow definition type:', typeof workflow.definition);
    
    // Check if this is the new format (with definition field)
    if (workflow.definition) {
      // Handle string definition (JSONB from PostgreSQL)
      let parsedDefinition = workflow.definition;
      if (typeof workflow.definition === 'string') {
        try {
          console.log(`Parsing definition for workflow ${workflow.id}:`, workflow.definition);
          // Handle already escaped JSON strings (with double backslashes)
          const definitionStr = workflow.definition.replace(/\\\\([\\"'])/g, '\\$1');
          parsedDefinition = JSON.parse(definitionStr);
          console.log('Parsed definition:', parsedDefinition);
        } catch (error) {
          console.error(`Error parsing definition for workflow ${workflow.id}:`, error);
          // Try one more time with a different approach for escaped JSON
          try {
            // The definition might be doubly escaped JSON string
            console.log('Trying alternative parsing method...');
            // This format: "{\"type\": \"new-request\", ...}"
            const cleanDefinition = workflow.definition.replace(/^"/, '').replace(/"$/, '');
            parsedDefinition = JSON.parse(cleanDefinition);
            console.log('Successfully parsed with alternative method:', parsedDefinition);
          } catch (secondError) {
            console.error('Alternative parsing also failed:', secondError);
            parsedDefinition = { type: 'unknown', steps: [], variant: 'unknown' };
          }
        }
      }
      
      // Extract workflow type and steps from the parsed definition
      const workflowType = parsedDefinition.type || 'unknown';
      const variant = parsedDefinition.variant || '';
      const steps = parsedDefinition.steps || [];
      
      console.log(`Workflow ${workflow.id} type:`, workflowType);
      console.log(`Workflow ${workflow.id} variant:`, variant);
      console.log(`Workflow ${workflow.id} steps:`, steps.length);
      if (steps.length > 0) {
        console.log('First step example:', steps[0]);
      }
      
      // Extract active status correctly
      const activeStatus = workflow.active !== undefined ? workflow.active : 
                          (workflow.isActive !== undefined ? workflow.isActive : true);
                          
      return {
        id: workflow.id || `wf-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        name: workflow.name,
        description: workflow.description,
        workflowType: workflowType,
        variant: variant,
        version: workflow.version || 1,
        isActive: activeStatus,
        steps: steps,
        stepsCount: steps.length,
        createdAt: workflow.createdAt || new Date().toISOString(),
        rawDefinition: parsedDefinition // Store the raw parsed definition for reference
      };
    }
    
    // Check if workflow has steps directly at the root level
    if (workflow.steps) {
      console.log('Workflow has steps at root level:', workflow.steps.length);
      workflow.steps.forEach((step, index) => {
        console.log(`Root level step ${index}:`, step);
      });
      return workflow;
    }
    
    // Try to create steps based on the sample format provided
    console.log('Attempting to create steps based on sample format');
    try {
      // Create sample steps based on the format provided
      const sampleSteps = [
        {
          id: "step-w01-001",
          name: "Initial Processing",
          serviceId: "service-001",
          isRequired: true,
          stepNumber: 1,
          serviceName: "THASHEEL",
          dependencies: [],
          executionMode: "SEQUENTIAL"
        },
        {
          id: "step-w01-002",
          name: "Document Verification",
          serviceId: "service-002",
          isRequired: true,
          stepNumber: 2,
          serviceName: "WPP",
          dependencies: ["step-w01-001"],
          executionMode: "SEQUENTIAL"
        }
      ];
      
      // Create a new workflow with the sample steps
      const workflowWithSteps = {
        ...workflow,
        steps: sampleSteps
      };
      
      console.log('Created workflow with sample steps:', workflowWithSteps);
      return workflowWithSteps;
    } catch (error) {
      console.error('Error creating sample steps:', error);
    }
    
    // Already in the expected format, but no steps
    console.log('Workflow already in expected format, but no steps');
    return {
      ...workflow,
      steps: []
    };
  };

  // Function to fetch version history with caching to prevent continuous API calls
  const fetchVersionHistory = useCallback(async () => {
    // Track if we've already attempted to fetch this workflow's version history
    // This prevents continuous retries on error
    const fetchAttemptKey = `version_history_fetch_attempt_${id}`;
    const hasFetchAttempt = sessionStorage.getItem(fetchAttemptKey);
    // Note: previously we skipped refetching if last attempt errored. This prevented recovery after endpoint fixes.
    // We now always attempt to fetch when invoked.
    
    try {
      setLoading(true);
      
      // Check if we have cached version history data in sessionStorage
      const cacheKey = `workflow_versions_${id}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      const cachedTimestamp = sessionStorage.getItem(`${cacheKey}_timestamp`);
      const currentTime = new Date().getTime();
      
      // Use cached data if it exists and is less than 5 minutes old
      if (cachedData && cachedTimestamp && (currentTime - parseInt(cachedTimestamp)) < 5 * 60 * 1000) {
        console.log('%c[DEBUG] Using cached version history data', 'background: #222; color: #bada55');
        const parsedVersions = JSON.parse(cachedData);
        setVersionHistory(parsedVersions);
        setLoading(false);
        return;
      }
      
      // Get the auth token from various possible storage keys
      const possibleTokenKeys = ['jwtToken', 'jwt_token', 'token', 'authToken', 'auth_token'];
      let token = null;
      
      for (const key of possibleTokenKeys) {
        const t = localStorage.getItem(key);
        if (t) {
          token = t;
          console.log(`Found token in ${key}:`, token.substring(0, 20) + '...');
          break;
        }
      }
      
      if (!token) {
        console.error('No token found in any of these keys:', possibleTokenKeys);
        // Mark as error to prevent continuous retries
        sessionStorage.setItem(fetchAttemptKey, 'error');
        throw new Error('No authentication token found');
      }
      
      // Call the version history API
      console.log('%c[DEBUG] Fetching version history data...', 'background: #222; color: #00ff00; font-weight: bold');
      const response = await fetch(`${API_BASE_URL}/api/workflow/${id}/versions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        // Mark as error to prevent continuous retries
        sessionStorage.setItem(fetchAttemptKey, 'error');
        // For 404 errors, just set empty version history and don't throw
        if (response.status === 404) {
          console.log('%c[DEBUG] No version history found (404)', 'background: #ff9900; color: #ffffff');
          setVersionHistory([]);
          return;
        }
        
        // For other errors, try to parse error data
        try {
          const errorData = await response.json();
          throw new Error(errorData.error || `Failed to fetch version history: ${response.status}`);
        } catch (parseError) {
          throw new Error(`Failed to fetch version history: ${response.status}`);
        }
      }
      
      const versions = await response.json();
      console.log('Version history:', versions);
      
      // Mark as successful and clear any prior error flag
      sessionStorage.setItem(fetchAttemptKey, 'success');
      
      // Cache the version history data
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(versions));
        sessionStorage.setItem(`${cacheKey}_timestamp`, currentTime.toString());
        console.log('%c[DEBUG] Version history data cached successfully', 'background: #222; color: #bada55');
      } catch (cacheError) {
        console.error('Error caching version history data:', cacheError);
      }
      
      // Sort versions by version number in descending order
      const sortedVersions = versions.sort((a, b) => b.versionNumber - a.versionNumber);
      setVersionHistory(sortedVersions);
    } catch (error) {
      console.error('Error fetching version history:', error);
      // Mark as error to prevent continuous retries
      sessionStorage.setItem(fetchAttemptKey, 'error');
      setVersionHistory([]);
    } finally {
      setLoading(false);
    }
  }, [id, API_PATHS.API]);

  // Function to view a specific version
  const handleViewVersion = (version) => {
    setSelectedVersion(version);
    setShowVersionDialog(true);
  };

  // Function to activate a previous version
  const handleActivateVersion = async (version) => {
    try {
      if (!window.confirm(`Are you sure you want to activate version ${version.versionNumber}? This will set it as the current version.`)) {
        return;
      }
      
      setLoading(true);
      
      // Get the auth token from various possible storage keys
      const possibleTokenKeys = ['jwtToken', 'jwt_token', 'token', 'authToken', 'auth_token'];
      let token = null;
      
      for (const key of possibleTokenKeys) {
        const t = localStorage.getItem(key);
        if (t) {
          token = t;
          console.log(`Found token in ${key}:`, token.substring(0, 20) + '...');
          break;
        }
      }
      
      if (!token) {
        console.error('No token found in any of these keys:', possibleTokenKeys);
        throw new Error('No authentication token found');
      }
      
      // Call the activate version API
      const response = await fetch(`${API_BASE_URL}/api/workflow/${id}/versions/${version.versionNumber}/activate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to activate version: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Version activation result:', result);
      
      // Show success message
      alert(`Version ${version.versionNumber} has been activated successfully.`);
      
      // Refresh workflow data and version history
      fetchWorkflowData();
      fetchVersionHistory();
    } catch (error) {
      console.error('Error activating version:', error);
      alert(`Failed to activate version: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Function to restore a previous version (continued)
  
  // Function to open the sync workflow dialog
  const handleSyncWorkflow = () => {
    setSyncNotes('');
    setSyncError(null);
    setSyncSuccess(false);
    setShowSyncDialog(true);
  };
  
  // Function to close the sync dialog
  const handleCloseSyncDialog = () => {
    setShowSyncDialog(false);
    // Reset field changes when closing the dialog
    setFieldChanges([]);
  };
  
  // Function to execute workflow synchronization
  const handleExecuteSync = async () => {
    try {
      setSyncLoading(true);
      setSyncError(null);
      
      // Get the auth token from various possible storage keys
      const possibleTokenKeys = ['jwtToken', 'jwt_token', 'token', 'authToken', 'auth_token'];
      let token = null;
      
      for (const key of possibleTokenKeys) {
        const t = localStorage.getItem(key);
        if (t) {
          token = t;
          console.log(`Found token in ${key}:`, token.substring(0, 20) + '...');
          break;
        }
      }
      
      if (!token) {
        console.error('No token found in any of these keys:', possibleTokenKeys);
        throw new Error('No authentication token found');
      }
      
      // Call the sync API
      const response = await fetch(`${API_PATHS.API}/workflows/${id}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: syncNotes })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to sync workflow: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Sync result:', result);
      
      // Extract field changes from the result
      const changes = result.fieldChanges || [];
      console.log('Field changes detected:', changes);
      
      // Update success state and field changes
      setSyncSuccess(true);
      setSyncLoading(false);
      setFieldChanges(changes);
      
      // Only close the dialog automatically if no changes were detected
      if (changes.length === 0) {
        // Refresh workflow data and version history after a short delay
        setTimeout(() => {
          fetchWorkflowData();
          fetchVersionHistory();
          handleCloseSyncDialog();
        }, 1500);
      } else {
        // Just refresh the data but keep the dialog open to show field changes
        fetchWorkflowData();
        fetchVersionHistory();
      }
      
    } catch (error) {
      console.error('Error syncing workflow:', error);
      setSyncError(error.message);
      setSyncLoading(false);
    }
  };

  // Function to get auth token from localStorage
  const getAuthToken = useCallback(() => {
    const possibleTokenKeys = ['jwtToken', 'jwt_token', 'token', 'authToken', 'auth_token'];
    let token = null;
    
    for (const key of possibleTokenKeys) {
      const t = localStorage.getItem(key);
      if (t) {
        token = t;
        break;
      }
    }
    
    if (!token) {
      console.error('No token found in any of these keys:', possibleTokenKeys);
      throw new Error('No authentication token found');
    }
    
    return token;
  }, []);

  // Function to fetch workflow assignments separately
  const fetchWorkflowAssignments = useCallback(async (skipLogging = false) => {
    if (!id) return;
    
    // Check if we've already fetched assignments for this workflow ID
    // This helps prevent repeated API calls
    const assignmentsCacheKey = `assignments_${id}`;
    const cachedAssignments = sessionStorage.getItem(assignmentsCacheKey);
    const cachedTimestamp = sessionStorage.getItem(`${assignmentsCacheKey}_timestamp`);
    const currentTime = new Date().getTime();
    
    // Use cached data if it exists and is less than 5 minutes old
    if (cachedAssignments && cachedTimestamp && (currentTime - parseInt(cachedTimestamp)) < 5 * 60 * 1000) {
      try {
        const parsedAssignments = JSON.parse(cachedAssignments);
        console.log('%c[DEBUG] Using cached assignments data', 'background: #222; color: #bada55');
        setAssignments(parsedAssignments);
        return;
      } catch (e) {
        console.error('Error parsing cached assignments:', e);
        // Continue with API fetch if cache parsing fails
      }
    }
    
    try {
      const token = getAuthToken();
      
      if (!skipLogging) {
        console.log('%c[DEBUG] Fetching assignments data from API...', 'background: #222; color: #00ff00; font-weight: bold');
      }
      
      const assignmentsResponse = await fetch(`${API_PATHS.API}/workflow-assignments/workflows/${id}/assignments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        // Add cache control headers to prevent browser caching
        cache: 'no-store'
      });
      
      if (!skipLogging) {
        console.log('%c[DEBUG] Assignments API response status:', 'background: #222; color: #bada55', assignmentsResponse.status);
      }
      
      if (assignmentsResponse.ok) {
        const assignmentsData = await assignmentsResponse.json();
        if (!skipLogging) {
          console.log('%c[DEBUG] Successfully fetched assignments from API:', 'background: #222; color: #00ff00', assignmentsData);
        }
        
        // Cache the assignments data with timestamp
        const currentTime = new Date().getTime();
        try {
          sessionStorage.setItem(assignmentsCacheKey, JSON.stringify(assignmentsData));
          sessionStorage.setItem(`${assignmentsCacheKey}_timestamp`, currentTime.toString());
          console.log('%c[DEBUG] Assignments data cached successfully with timestamp', 'background: #222; color: #bada55');
        } catch (e) {
          console.error('Error caching assignments:', e);
        }
        
        setAssignments(assignmentsData);
      } else {
        console.error('%c[DEBUG] Failed to fetch assignments:', 'background: #ff0000; color: #ffffff', assignmentsResponse.status);
        setAssignments([]);
      }
    } catch (assignError) {
      console.error('%c[DEBUG] Error fetching assignments:', 'background: #ff0000; color: #ffffff', assignError);
      setAssignments([]);
    }
  }, [id, API_PATHS.API, getAuthToken]);

  // Function to fetch workflow data with caching to prevent continuous API calls
  const fetchWorkflowData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Always fetch fresh workflow data to avoid stale name/version/steps post-edit
      const cacheKey = `workflow_data_${id}`;
      const currentTime = new Date().getTime();
      
      // Check if this is the target workflow for debugging
      const isTargetWorkflow = id === 'SGGroupTestWK01';
      if (isTargetWorkflow) {
        console.log('Target workflow found:', id);
      }
      
      const token = getAuthToken();
      console.log('[DEBUG] Found token:', token.substring(0, 20) + '...');

      // Use the new secured API endpoint aligned with versioning services
      console.log('%c[DEBUG] Fetching workflow data (fresh, new API)...', 'background: #222; color: #00ff00; font-weight: bold');
      const response = await fetch(`${API_BASE_URL}/api/workflow/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        cache: 'no-store'
      });
      console.log('%c[DEBUG] API response status:', 'background: #222; color: #bada55', response.status);
      
      if (!response.ok) {
        console.error('%c[DEBUG] API response not OK:', 'background: #ff0000; color: #ffffff', response.status);
        throw new Error(`Failed to fetch workflow: ${response.status} ${response.statusText}`);
      }
      
      const apiData = await response.json();
      console.log('%c[DEBUG] API data received:', 'background: #222; color: #00ff00', apiData);
      console.log('%c[DEBUG] API data definition:', 'background: #222; color: #00ff00', apiData.definition);
      console.log('%c[DEBUG] Using REAL DATA from API', 'background: #222; color: #00ff00; font-weight: bold');
      
      // Cache the workflow data
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(apiData));
        sessionStorage.setItem(`${cacheKey}_timestamp`, currentTime.toString());
        console.log('%c[DEBUG] Workflow data cached successfully', 'background: #222; color: #bada55');
      } catch (cacheError) {
        console.error('Error caching workflow data:', cacheError);
      }
      
      if (isTargetWorkflow) {
        console.log('Target workflow definition found:', apiData.definition);
        // Log the definition in a more readable format
        try {
          const parsedDef = typeof apiData.definition === 'string' ? 
            JSON.parse(apiData.definition) : apiData.definition;
          console.log('Parsed definition:', parsedDef);
          console.log('Steps:', parsedDef.steps);
        } catch (parseError) {
          console.error('Error parsing definition:', parseError);
        }
      }
      
      // Normalize the workflow data
      const normalizedData = normalizeWorkflowData(apiData);
      setWorkflow(normalizedData);
      setWorkflowJson(apiData);
      
      // Fetch assignments only once during initial load
      // We'll use a separate useEffect for this
      
    } catch (error) {
      console.error('%c[DEBUG] Error fetching workflow data:', 'background: #ff0000; color: #ffffff', error);
      setError('Failed to fetch workflow data from the API. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [id, API_PATHS.V1, normalizeWorkflowData, getAuthToken]);

  // Initialize workflow data and version history when component mounts
  // Use a ref to ensure we only fetch data once per workflow ID
  const dataFetchedRef = React.useRef(false);
  
  useEffect(() => {
    // Reset the ref when the ID changes
    if (id !== dataFetchedRef.current) {
      console.log('%c[DEBUG] Initial data fetch for workflow', 'background: #222; color: #00ff00; font-weight: bold');
      fetchWorkflowData();
      fetchVersionHistory();
      dataFetchedRef.current = id; // Store the current ID to prevent refetching
    } else {
      console.log('%c[DEBUG] Skipping duplicate data fetch for workflow', 'background: #222; color: #bada55');
    }
    
    // Cleanup function to prevent memory leaks
    return () => {
      console.log('%c[DEBUG] Cleaning up workflow data fetch effect', 'background: #222; color: #bada55');
    };
  }, [id, fetchWorkflowData, fetchVersionHistory]); // Include these to avoid lint warnings
  
  // Ref for tracking initial assignments fetch
  const initialAssignmentFetchRef = React.useRef({});
  
  // Separate effect for fetching assignments to prevent UI flickering
  useEffect(() => {
    // Only fetch assignments once per workflow ID
    if (id && !initialAssignmentFetchRef.current[id]) {
      console.log('%c[DEBUG] Initial assignments fetch for workflow ID:', 'background: #222; color: #00ff00; font-weight: bold', id);
      fetchWorkflowAssignments();
      // Mark this workflow ID as fetched
      initialAssignmentFetchRef.current[id] = true;
    } else if (id) {
      console.log('%c[DEBUG] Skipping duplicate assignments fetch for workflow ID:', 'background: #222; color: #bada55', id);
    }
  }, [id, fetchWorkflowAssignments]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleBack = () => {
    // Check the current path to determine which route pattern to use for navigation
    const currentPath = window.location.pathname;
    if (currentPath.includes('ADM-Workflow')) {
      navigate('/ADM-Workflow/list');
    } else {
      navigate('/admin/workflow/list');
    }
  };

  const handleEdit = () => {
    try {
      // Prefer the raw API object if available; fallback to normalized
      const parseDef = (def) => {
        if (!def) return {};
        if (typeof def === 'object') return def;
        try {
          return JSON.parse(def);
        } catch (e) {
          console.warn('Failed to parse string definition, sending empty object');
          return {};
        }
      };

      let payload = null;
      if (workflowJson) {
        payload = {
          id: workflowJson.id,
          name: workflowJson.name,
          description: workflowJson.description,
          targetType: workflowJson.targetType || 'EMPLOYEE',
          definition: parseDef(workflowJson.definition)
        };
      } else if (workflow) {
        payload = {
          id: workflow.id,
          name: workflow.name,
          description: workflow.description,
          targetType: workflow.targetType || 'EMPLOYEE',
          definition: parseDef(workflow.rawDefinition || workflow.definition)
        };
      }
      if (!payload) {
        alert('Workflow data not ready yet. Please try again in a moment.');
        return;
      }
      // Persist for the editor to pick up in BasicWorkflowCreator
      localStorage.setItem('workflow_to_edit', JSON.stringify(payload));
      navigate(`/admin/workflow/${id}/edit`);
    } catch (e) {
      console.error('Failed to prepare workflow for edit:', e);
      alert('Unable to open editor: failed to prepare workflow data.');
    }
  };

  const handleAssign = () => {
    navigate(`/admin/workflow/${id}/assign`);
  };

  const handleDuplicate = async () => {
    try {
      const duplicatedWorkflow = {
        ...workflow,
        id: undefined,
        name: `${workflow.name} (Copy)`,
        version: workflow.version + 1
      };
      
      // In a real implementation, this would call the API to create a new workflow
      // For now, we'll just store it in localStorage
      try {
        const savedWorkflows = JSON.parse(localStorage.getItem('thaklis_workflows') || '[]');
        const newId = `wf-${Date.now()}`;
        const newWorkflow = {
          ...duplicatedWorkflow,
          id: newId
        };
        savedWorkflows.push(newWorkflow);
        localStorage.setItem('thaklis_workflows', JSON.stringify(savedWorkflows));
        
        // Navigate to the new workflow
        navigate(`/admin/workflow/${newId}`);
      } catch (storageError) {
        console.error('Error saving to localStorage:', storageError);
        setError('Failed to duplicate workflow. Please try again.');
      }
    } catch (error) {
      console.error('Error duplicating workflow:', error);
      setError('Failed to duplicate workflow. Please try again.');
    }
  };

  // Helper function to get dependencies for a step
  const getDependenciesForStep = (stepId) => {
    if (!workflow) return [];
    
    const dependencies = [];
    workflow.steps.forEach(step => {
      step.dependencies.forEach(dep => {
        if (dep.stepId === stepId) {
          const dependentStep = workflow.steps.find(s => s.id === step.id);
          if (dependentStep) {
            dependencies.push({
              stepId: step.id,
              stepName: dependentStep.name,
              type: dep.type
            });
          }
        }
      });
    });
    
    return dependencies;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!workflow) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Workflow not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to Workflows
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          {workflow.name}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Chip
          label={workflow.workflowType}
          color="primary"
          variant="outlined"
          sx={{ mr: 1 }}
        />
        <Chip
          label={workflow.isActive ? 'Active' : 'Inactive'}
          color={workflow.isActive ? 'success' : 'default'}
          sx={{ mr: 1 }}
        />
        <Typography variant="body2" color="textSecondary" sx={{ mr: 2 }}>
          Version: {workflow.version}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Edit Workflow">
          <IconButton color="primary" onClick={handleEdit}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Assign Workflow">
          <IconButton color="primary" onClick={handleAssign}>
            <AssignmentIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Duplicate Workflow">
          <IconButton color="primary" onClick={handleDuplicate}>
            <DuplicateIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Sync Workflow with Latest Service Groups">
          <IconButton color="secondary" onClick={handleSyncWorkflow}>
            <CompareIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
          <Tab label="Overview" />
          <Tab label="Steps" />
          <Tab label="Assignments" />
          <Tab label="Version History" icon={<HistoryIcon />} iconPosition="start" />
          <Tab label="JSON" />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {tabValue === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Workflow Details
              </Typography>
              <Typography variant="body1" paragraph>
                {workflow.description}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Statistics
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Paper elevation={1} sx={{ p: 2, minWidth: 150 }}>
                  <Typography variant="body2" color="textSecondary">
                    Total Steps
                  </Typography>
                  <Typography variant="h6">
                    {workflow.steps.length}
                  </Typography>
                </Paper>
                <Paper elevation={1} sx={{ p: 2, minWidth: 150 }}>
                  <Typography variant="body2" color="textSecondary">
                    Assigned To
                  </Typography>
                  <Typography variant="h6">
                    {assignments.length} Organizations
                  </Typography>
                </Paper>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                Metadata
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <Typography variant="body2">
                  <strong>Created By:</strong> {workflow.createdBy}
                </Typography>
                <Typography variant="body2">
                  <strong>Created Date:</strong> {new Date(workflow.createDateTime).toLocaleString()}
                </Typography>
                {workflow.modifyDateTime && (
                  <>
                    <Typography variant="body2">
                      <strong>Modified By:</strong> {workflow.modifiedBy}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Modified Date:</strong> {new Date(workflow.modifyDateTime).toLocaleString()}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
          
          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Steps Visualization
              </Typography>
              
              {/* Debug information */}
              <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>Debug Information:</Typography>
                <Typography variant="body2">Workflow ID: {workflow?.id}</Typography>
                <Typography variant="body2">Steps Array: {workflow?.steps ? `Found (${workflow.steps.length} steps)` : 'Not found'}</Typography>
                <Typography variant="body2">Raw Definition: {workflow?.rawDefinition ? 'Available' : 'Not available'}</Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  sx={{ mt: 1 }}
                  onClick={() => {
                    console.log('Current workflow state:', workflow);
                    console.log('Steps:', workflow?.steps);
                    console.log('Raw definition:', workflow?.rawDefinition);
                    alert('Check browser console for detailed workflow information');
                  }}
                >
                  Log Details to Console
                </Button>
              </Box>
              
              {workflow?.steps && workflow.steps.length > 0 ? (
                workflow.steps.map((step, index) => {
                  console.log(`Rendering step ${index}:`, step);
                  
                  // Process dependencies based on the actual format
                  let dependencies = [];
                  
                  if (step.dependencies) {
                    console.log(`Dependencies for step ${index}:`, step.dependencies);
                    
                    // Handle dependencies as array of strings (step IDs)
                    if (Array.isArray(step.dependencies) && step.dependencies.length > 0 && typeof step.dependencies[0] === 'string') {
                      console.log(`Step ${index} has string dependencies`);
                      dependencies = step.dependencies.map(depId => {
                        const dependencyStep = workflow.steps.find(s => s.id === depId);
                        console.log(`Found dependency step for ${depId}:`, dependencyStep);
                        return {
                          stepId: depId,
                          stepName: dependencyStep ? dependencyStep.name : 'Unknown Step',
                          type: 'FINISH_TO_START' // Default type
                        };
                      });
                    } 
                    // Handle dependencies as array of objects
                    else if (Array.isArray(step.dependencies) && step.dependencies.length > 0 && typeof step.dependencies[0] === 'object') {
                      console.log(`Step ${index} has object dependencies`);
                      dependencies = step.dependencies.map(dep => {
                        const dependencyStep = workflow.steps.find(s => s.id === dep.stepId);
                        console.log(`Found dependency step for ${dep.stepId}:`, dependencyStep);
                        return {
                          stepId: dep.stepId,
                          stepName: dependencyStep ? dependencyStep.name : 'Unknown Step',
                          type: dep.type || 'FINISH_TO_START'
                        };
                      });
                    }
                  } else {
                    console.log(`Step ${index} has no dependencies`);
                  }
                  
                  // Create a normalized step object for the card
                  const normalizedStep = {
                    ...step,
                    stepNumber: step.stepNumber || 0,
                    isRequired: step.isRequired !== undefined ? step.isRequired : true
                  };
                  
                  console.log(`Normalized step ${index}:`, normalizedStep);
                  console.log(`Dependencies for step ${index}:`, dependencies);
                  
                  return (
                    <WorkflowStepCard
                      key={step.id || `step-${index}`}
                      step={normalizedStep}
                      dependencies={dependencies}
                    />
                  );
                })
              ) : (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    This workflow has no steps defined.
                  </Typography>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={() => {
                      // Try to manually parse the definition if available in workflowJson
                      if (workflowJson && workflowJson.definition) {
                        console.log('Attempting manual parsing of definition...');
                        try {
                          let parsedDef;
                          if (typeof workflowJson.definition === 'string') {
                            parsedDef = JSON.parse(workflowJson.definition);
                          } else {
                            parsedDef = workflowJson.definition;
                          }
                          
                          console.log('Manually parsed definition:', parsedDef);
                          if (parsedDef.steps && parsedDef.steps.length > 0) {
                            console.log('Found steps in manual parsing:', parsedDef.steps);
                            const updatedWorkflow = {
                              ...workflow,
                              steps: parsedDef.steps
                            };
                            console.log('Updated workflow with steps:', updatedWorkflow);
                            setWorkflow(updatedWorkflow);
                            alert('Steps have been manually loaded. Please check the view again.');
                          } else {
                            alert('No steps found in the definition after manual parsing.');
                          }
                        } catch (error) {
                          console.error('Error in manual parsing:', error);
                          alert('Error parsing the definition: ' + error.message);
                        }
                      } else {
                        alert('No definition data available for manual parsing.');
                      }
                    }}
                  >
                    Attempt Manual Step Loading
                  </Button>
                </>
              )}
            </Box>
          )}
          
          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Workflow Assignments
              </Typography>
              {assignments.length === 0 ? (
                <Typography variant="body1">
                  This workflow has not been assigned to any customer organizations yet.
                </Typography>
              ) : (
                assignments.map((assignment) => (
                  <Paper key={assignment.id} sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle1">
                      {assignment.customerName}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Status: {assignment.isActive ? 'Active' : 'Inactive'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Assigned on: {new Date(assignment.createDateTime).toLocaleDateString()}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => navigate(`/admin/workflow/assignment/${assignment.id}`)}
                    >
                      View Details
                    </Button>
                  </Paper>
                ))
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleAssign}
                sx={{ mt: 2 }}
              >
                Assign to Customer
              </Button>
            </Box>
          )}
          
          {tabValue === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Version History
              </Typography>
              {versionHistory.length === 0 ? (
                <Typography variant="body1">
                  No version history available for this workflow.
                </Typography>
              ) : (
                <List>
                  {versionHistory.map((version, index) => (
                    <ListItem key={index} divider={index < versionHistory.length - 1}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="subtitle1">
                              Version {version.versionNumber}
                            </Typography>
                            {version.isCurrent && (
                              <Chip 
                                label="Current" 
                                color="primary" 
                                size="small" 
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" component="span">
                              Created: {new Date(version.createdAt).toLocaleString()}
                            </Typography>
                            <br />
                            <Typography variant="body2" component="span">
                              By: {resolveCreatedBy(version.createdBy)}
                            </Typography>
                            {version.notes && (
                              <>
                                <br />
                                <Typography variant="body2" component="span">
                                  Notes: {version.notes}
                                </Typography>
                              </>
                            )}
                            {version.syncSource && (
                              <>
                                <br />
                                <Typography variant="body2" component="span">
                                  Sync Source: {version.syncSource}
                                </Typography>
                              </>
                            )}
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="View Version">
                          <IconButton edge="end" onClick={() => handleViewVersion(version)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {!version.isCurrent && (
                          <Tooltip title="Activate Version">
                            <IconButton edge="end" onClick={() => handleActivateVersion(version)}>
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
          
          {tabValue === 4 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                JSON Editor
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                This is a read-only view of the workflow JSON. To edit, click the Edit button above.
              </Typography>
              <JsonEditor value={workflowJson} onChange={setWorkflowJson} readOnly={true} />
            </Box>
          )}
        </Box>
      </Paper>

      {/* Version History Dialog */}
      <Dialog
        open={showVersionDialog}
        onClose={() => setShowVersionDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Workflow Version {selectedVersion?.versionNumber}
          <IconButton
            aria-label="close"
            onClick={() => setShowVersionDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <ArrowBackIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedVersion && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Version Details
                </Typography>
                <Typography variant="body2">
                  <strong>Created:</strong> {new Date(selectedVersion.createdAt).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Created By:</strong> {resolveCreatedBy(selectedVersion.createdBy)}
                </Typography>
                {selectedVersion.notes && (
                  <Typography variant="body2">
                    <strong>Notes:</strong> {selectedVersion.notes}
                  </Typography>
                )}
                {selectedVersion.syncSource && (
                  <Typography variant="body2">
                    <strong>Sync Source:</strong> {selectedVersion.syncSource}
                  </Typography>
                )}
                <Typography variant="body2">
                  <strong>Status:</strong> {selectedVersion.isCurrent ? 'Current Version' : 'Previous Version'}
                </Typography>
              </Box>
              <Typography variant="subtitle1" gutterBottom>
                JSON Definition
              </Typography>
              <JsonEditor 
                value={selectedVersion.jsonDefinition ? 
                  (typeof selectedVersion.jsonDefinition === 'string' ? 
                    JSON.parse(selectedVersion.jsonDefinition) : 
                    selectedVersion.jsonDefinition) : 
                  {}} 
                onChange={() => {}} 
                readOnly={true} 
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVersionDialog(false)}>Close</Button>
          {selectedVersion && !selectedVersion.isCurrent && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => {
                handleActivateVersion(selectedVersion);
                setShowVersionDialog(false);
              }}
            >
              Activate This Version
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Sync Workflow Dialog */}
      <Dialog open={showSyncDialog} onClose={handleCloseSyncDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {syncSuccess ? 'Workflow Synchronized' : 'Synchronize Workflow'}
        </DialogTitle>
        <DialogContent>
          {syncLoading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : syncSuccess ? (
            <>
              <Typography gutterBottom>
                Workflow has been successfully synchronized with the latest service group definitions.
              </Typography>
              
              {fieldChanges && fieldChanges.length > 0 ? (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    Field Changes Detected
                  </Typography>
                  
                  {/* Summary Section */}
                  <Box mb={3} sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Summary of Changes
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 1, 
                            bgcolor: 'success.light', 
                            color: 'success.contrastText',
                            textAlign: 'center',
                            borderRadius: 1
                          }}
                        >
                          <Typography variant="h6">
                            {fieldChanges.reduce((total, change) => total + (change.addedFields?.length || 0), 0)}
                          </Typography>
                          <Typography variant="body2">Added Fields</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 1, 
                            bgcolor: 'error.light', 
                            color: 'error.contrastText',
                            textAlign: 'center',
                            borderRadius: 1
                          }}
                        >
                          <Typography variant="h6">
                            {fieldChanges.reduce((total, change) => total + (change.removedFields?.length || 0), 0)}
                          </Typography>
                          <Typography variant="body2">Removed Fields</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Paper 
                          elevation={0} 
                          sx={{ 
                            p: 1, 
                            bgcolor: 'primary.light', 
                            color: 'primary.contrastText',
                            textAlign: 'center',
                            borderRadius: 1
                          }}
                        >
                          <Typography variant="h6">
                            {fieldChanges.reduce((total, change) => total + (change.modifiedFields?.length || 0), 0)}
                          </Typography>
                          <Typography variant="body2">Modified Fields</Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Changes detected across {fieldChanges.length} service group{fieldChanges.length !== 1 ? 's' : ''}.
                    </Typography>
                  </Box>
                  
                  {fieldChanges.map((change, index) => (
                    <Box key={index} mb={3} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {change.stepTitle || 'Step'} ({change.serviceGroupName || 'Service Group'})
                      </Typography>
                      
                      {/* Added Fields */}
                      {change.addedFields && change.addedFields.length > 0 && (
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="success.main" gutterBottom>
                            Added Fields ({change.addedFields.length})
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Field Name</TableCell>
                                  <TableCell>Type</TableCell>
                                  <TableCell>Required</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {change.addedFields.map((field, fieldIndex) => (
                                  <TableRow key={fieldIndex}>
                                    <TableCell>{field.title}</TableCell>
                                    <TableCell>{field.type}</TableCell>
                                    <TableCell>{field.mandatory ? 'Yes' : 'No'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                      
                      {/* Removed Fields */}
                      {change.removedFields && change.removedFields.length > 0 && (
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="error.main" gutterBottom>
                            Removed Fields ({change.removedFields.length})
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Field Name</TableCell>
                                  <TableCell>Type</TableCell>
                                  <TableCell>Required</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {change.removedFields.map((field, fieldIndex) => (
                                  <TableRow key={fieldIndex}>
                                    <TableCell>{field.title}</TableCell>
                                    <TableCell>{field.type}</TableCell>
                                    <TableCell>{field.mandatory ? 'Yes' : 'No'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                      
                      {/* Modified Fields */}
                      {change.modifiedFields && change.modifiedFields.length > 0 && (
                        <Box mt={2}>
                          <Typography variant="subtitle2" color="primary.main" gutterBottom>
                            Modified Fields ({change.modifiedFields.length})
                          </Typography>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Field Name</TableCell>
                                  <TableCell>Change Type</TableCell>
                                  <TableCell>Old Value</TableCell>
                                  <TableCell>New Value</TableCell>
                                  <TableCell>Details</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {change.modifiedFields.map((field, fieldIndex) => (
                                  <TableRow key={fieldIndex} sx={{
                                    backgroundColor: field.changeType === 'REQUIRED_CHANGED' ? '#fff8e1' : 
                                                   field.changeType === 'TYPE_CHANGED' ? '#e3f2fd' : 
                                                   field.changeType === 'OPTIONS_CHANGED' ? '#f3e5f5' : 
                                                   field.changeType === 'DEFAULT_VALUE_CHANGED' ? '#e8f5e9' :
                                                   field.changeType === 'VALIDATION_CHANGED' ? '#fce4ec' :
                                                   'inherit'
                                  }}>
                                    <TableCell>
                                      <Tooltip title={`Field ID: ${field.id || 'N/A'}`}>
                                        <Typography variant="body2">
                                          <strong>{field.title}</strong>
                                          {field.fieldKey && (
                                            <Typography variant="caption" display="block" color="text.secondary">
                                              Key: {field.fieldKey}
                                            </Typography>
                                          )}
                                        </Typography>
                                      </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                      <Chip 
                                        size="small" 
                                        label={field.changeType.replace('_', ' ')} 
                                        color={
                                          field.changeType === 'REQUIRED_CHANGED' ? 'warning' : 
                                          field.changeType === 'TYPE_CHANGED' ? 'primary' : 
                                          field.changeType === 'OPTIONS_CHANGED' ? 'secondary' : 
                                          field.changeType === 'DEFAULT_VALUE_CHANGED' ? 'success' :
                                          field.changeType === 'VALIDATION_CHANGED' ? 'error' :
                                          'default'
                                        }
                                        variant="outlined"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {field.changeType === 'OPTIONS_CHANGED' ? (
                                        <Tooltip title={Array.isArray(field.oldValue) ? field.oldValue.join(', ') : String(field.oldValue)}>
                                          <Typography variant="body2" noWrap sx={{ maxWidth: '150px' }}>
                                            {Array.isArray(field.oldValue) 
                                              ? `[${field.oldValue.length} options]` 
                                              : (typeof field.oldValue === 'boolean' 
                                                ? String(field.oldValue) 
                                                : field.oldValue)}
                                          </Typography>
                                        </Tooltip>
                                      ) : (
                                        <Typography variant="body2">
                                          {typeof field.oldValue === 'boolean' 
                                            ? String(field.oldValue) 
                                            : field.oldValue}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {field.changeType === 'OPTIONS_CHANGED' ? (
                                        <Tooltip title={Array.isArray(field.newValue) ? field.newValue.join(', ') : String(field.newValue)}>
                                          <Typography variant="body2" noWrap sx={{ maxWidth: '150px' }}>
                                            {Array.isArray(field.newValue) 
                                              ? `[${field.newValue.length} options]` 
                                              : (typeof field.newValue === 'boolean' 
                                                ? String(field.newValue) 
                                                : field.newValue)}
                                          </Typography>
                                        </Tooltip>
                                      ) : (
                                        <Typography variant="body2">
                                          {typeof field.newValue === 'boolean' 
                                            ? String(field.newValue) 
                                            : field.newValue}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <IconButton 
                                        size="small" 
                                        onClick={() => {
                                          // Log detailed field change information for debugging
                                          console.log('Field change details:', field);
                                          
                                          // Create a detailed dialog for field changes
                                          const detailsDialog = document.createElement('div');
                                          detailsDialog.style.position = 'fixed';
                                          detailsDialog.style.top = '50%';
                                          detailsDialog.style.left = '50%';
                                          detailsDialog.style.transform = 'translate(-50%, -50%)';
                                          detailsDialog.style.backgroundColor = 'white';
                                          detailsDialog.style.padding = '20px';
                                          detailsDialog.style.borderRadius = '8px';
                                          detailsDialog.style.boxShadow = '0 4px 20px rgba(0,0,0,0.2)';
                                          detailsDialog.style.zIndex = '9999';
                                          detailsDialog.style.maxWidth = '80%';
                                          detailsDialog.style.maxHeight = '80%';
                                          detailsDialog.style.overflow = 'auto';
                                          
                                          // Create content for the dialog
                                          let content = `
                                            <h3>Field Change Details</h3>
                                            <p><strong>Field:</strong> ${field.title}</p>
                                            <p><strong>Change Type:</strong> ${field.changeType.replace('_', ' ')}</p>
                                            <p><strong>Field ID:</strong> ${field.id || 'N/A'}</p>
                                            <p><strong>Field Key:</strong> ${field.fieldKey || 'N/A'}</p>
                                            <hr/>
                                          `;
                                          
                                          // Add old and new values with proper formatting
                                          if (field.changeType === 'OPTIONS_CHANGED' && Array.isArray(field.oldValue)) {
                                            content += `<p><strong>Old Options:</strong></p><ul>`;
                                            field.oldValue.forEach(option => {
                                              content += `<li>${option}</li>`;
                                            });
                                            content += `</ul>`;
                                            
                                            content += `<p><strong>New Options:</strong></p><ul>`;
                                            if (Array.isArray(field.newValue)) {
                                              field.newValue.forEach(option => {
                                                content += `<li>${option}</li>`;
                                              });
                                            }
                                            content += `</ul>`;
                                          } else {
                                            content += `<p><strong>Old Value:</strong> ${field.oldValue}</p>`;
                                            content += `<p><strong>New Value:</strong> ${field.newValue}</p>`;
                                          }
                                          
                                          // Add additional details if available
                                          if (field.additionalDetails) {
                                            content += `<hr/><p><strong>Additional Details:</strong></p>`;
                                            content += `<p>${field.additionalDetails}</p>`;
                                          }
                                          
                                          // Add close button
                                          content += `
                                            <div style="text-align: right; margin-top: 20px;">
                                              <button id="closeFieldDetailsDialog" style="padding: 8px 16px; background-color: #1976d2; color: white; border: none; border-radius: 4px; cursor: pointer;">Close</button>
                                            </div>
                                          `;
                                          
                                          detailsDialog.innerHTML = content;
                                          document.body.appendChild(detailsDialog);
                                          
                                          // Add overlay
                                          const overlay = document.createElement('div');
                                          overlay.style.position = 'fixed';
                                          overlay.style.top = '0';
                                          overlay.style.left = '0';
                                          overlay.style.width = '100%';
                                          overlay.style.height = '100%';
                                          overlay.style.backgroundColor = 'rgba(0,0,0,0.5)';
                                          overlay.style.zIndex = '9998';
                                          document.body.appendChild(overlay);
                                          
                                          // Add close functionality
                                          document.getElementById('closeFieldDetailsDialog').addEventListener('click', () => {
                                            document.body.removeChild(detailsDialog);
                                            document.body.removeChild(overlay);
                                          });
                                          
                                          // Close on overlay click
                                          overlay.addEventListener('click', () => {
                                            document.body.removeChild(detailsDialog);
                                            document.body.removeChild(overlay);
                                          });
                                        }}
                                      >
                                        <InfoIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography sx={{ mt: 2 }} color="text.secondary">
                  No field changes were detected. The workflow is already up to date.
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography gutterBottom>
                This will synchronize the workflow with the latest service group definitions. Any changes in the service groups will be reflected in this workflow.
              </Typography>
              <TextField
                label="Sync Notes (Optional)"
                multiline
                rows={3}
                fullWidth
                margin="normal"
                value={syncNotes}
                onChange={(e) => setSyncNotes(e.target.value)}
              />
            </>
          )}
          {syncError && (
            <Typography color="error" sx={{ mt: 2 }}>
              Error: {syncError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {syncLoading ? null : (
            <Button onClick={handleCloseSyncDialog} color="primary">
              Close
            </Button>
          )}
          {!syncLoading && !syncSuccess && (
            <Button onClick={handleExecuteSync} color="primary" variant="contained">
              Synchronize
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowDetail;
